import React from "react";

const formatWhatsAppText = (text, samples = {}) => {
  if (!text) return "";
  let formatted = text
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
    
  // Replace variables {{1}}, {{2}} with sample values
  formatted = formatted.replace(/{{(\d+)}}/g, (match, num) => {
    const key = `{{${num}}}`;
    // Use the sample value if it exists and is not empty, otherwise keep the placeholder
    return (samples[key] && samples[key].trim() !== "") ? samples[key] : match;
  });

  formatted = formatted
    // Replace ```monospace```
    .replace(/```([\s\S]*?)```/g, '<code style="font-family: monospace; background: rgba(0,0,0,0.05); padding: 2px 4px; border-radius: 4px;">$1</code>')
    // Replace *bold*
    .replace(/\*([^\*]+)\*/g, '<strong>$1</strong>')
    // Replace _italic_
    .replace(/_([^_]+)_/g, '<em>$1</em>')
    // Replace ~strikethrough~
    .replace(/~([^~]+)~/g, '<del>$1</del>');
    
  return formatted;
};

export default function WhatsAppPreview({
  templateName = "template_name",
  category = "MARKETING",
  headerType = "NONE",
  headerText = "",
  headerPreview = null,
  mediaFileName = "",
  mediaFileSize = "",
  locationData = null,
  bodyText = "",
  footerText = "",
  buttons = [],
  samples = {},
  // Authentication-specific options
  isAuthentication = false,
  authOtpType = "COPY_CODE", // COPY_CODE | ONE_TAP | ZERO_TAP
  authCopyCodeText = "Copy Code",
  authAutofillText = "Autofill",
  addSecurityRecommendation = true,
  addExpiryTime = true,
  codeExpirationMinutes = 10,
}) {
  const isAuth = isAuthentication || String(category).toUpperCase() === "AUTHENTICATION";

  // Build simulated verification message for authentication
  const authCodeSample = samples["{{1}}"] || "797011";
  const authBody = `${authCodeSample} is your verification code.${
    addSecurityRecommendation ? " For your security, do not share this code." : ""
  }`;
  const authFooter = addExpiryTime ? `This code expires in ${codeExpirationMinutes || 10} minutes.` : "";
  return (
    <div
      style={{
        width: "250px",
        height: "450px",
        backgroundColor: "#000",
        borderRadius: "28px",
        padding: "3px",
        boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
        position: "relative",
      }}
    >
      <style>{`
        .whatsapp-preview-chat-scroll::-webkit-scrollbar {
          width: 4px;
        }
        .whatsapp-preview-chat-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .whatsapp-preview-chat-scroll::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.22);
          border-radius: 4px;
        }
        .whatsapp-preview-chat-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.4);
        }
        .whatsapp-preview-chat-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(0, 0, 0, 0.22) transparent;
        }
      `}</style>

      <div
        style={{
          width: "100%",
          height: "100%",
          backgroundColor: "#e5ddd5",
          borderRadius: "25px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* STATUS BAR */}
        <div
          style={{
            height: "30px",
            backgroundColor: "#075e54",
            color: "white",
            fontSize: "11px",
            padding: "0 12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <span>9:41</span>
          <span>📶 📶 🔋</span>
        </div>

        {/* CHAT HEADER */}
        <div
          style={{
            backgroundColor: "#075e54",
            padding: "6px 12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: "28px",
              height: "28px",
              backgroundColor: "#facc15",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              fontWeight: "bold",
            }}
          >
            A
          </div>
          <div>
            <div style={{ fontSize: "13px", color: "#fff" }}>account</div>
            <div style={{ fontSize: "10px", color: "#d1fae5" }}>online</div>
          </div>
        </div>

        {/* CHAT AREA (SCROLLABLE INSIDE) */}
        <div
          className="whatsapp-preview-chat-scroll"
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            padding: "8px 10px",
          }}
        >
          <div
            style={{
              backgroundColor: "#dcf8c6",
              borderRadius: "6px",
              padding: "8px",
              maxWidth: "88%",
              marginLeft: "auto",
              boxShadow: "0 1px 1px rgba(0,0,0,0.12)",
              wordBreak: "break-word",
            }}
          >
            {/* TEMPLATE NAME */}
            <div style={{ fontSize: "9px", color: "#555", marginBottom: "4px" }}>
              {templateName}
            </div>

            {/* HEADER */}
            {!isAuth && headerType === "TEXT" && headerText && (
              <div style={{ fontWeight: "600", marginBottom: "6px" }}>
                {headerText}
              </div>
            )}

            {/* IMAGE */}
            {!isAuth && headerType === "IMAGE" && (
              <div
                style={{
                  width: "100%",
                  height: "100px",
                  borderRadius: "6px",
                  overflow: "hidden",
                  backgroundColor: "#e2e8f0",
                  marginBottom: "8px",
                  border: "1px solid #cbd5e1",
                  position: "relative",
                }}
              >
                <img
                  src={
                    headerPreview ||
                    "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=500&auto=format&fit=crop&q=70"
                  }
                  alt="preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </div>
            )}

            {/* VIDEO */}
            {!isAuth && headerType === "VIDEO" && (
              <div
                style={{
                  width: "100%",
                  height: "100px",
                  borderRadius: "6px",
                  overflow: "hidden",
                  backgroundColor: "#0f172a",
                  marginBottom: "8px",
                  border: "1px solid #cbd5e1",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {headerPreview ? (
                  <video
                    src={headerPreview}
                    controls
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      color: "#fff",
                    }}
                  >
                    <div
                      style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        backgroundColor: "rgba(255,255,255,0.25)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "14px",
                        marginBottom: "4px",
                      }}
                    >
                      ▶
                    </div>
                    <span style={{ fontSize: "9px", color: "#cbd5e1" }}>
                      Video Preview (0:30)
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* DOCUMENT */}
            {!isAuth && headerType === "DOCUMENT" && (
              <div
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "6px",
                  padding: "8px",
                  marginBottom: "8px",
                  border: "1px solid #cbd5e1",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "6px",
                    backgroundColor: "#fee2e2",
                    color: "#dc2626",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "bold",
                    fontSize: "10px",
                  }}
                >
                  PDF
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: "600",
                      color: "#1e293b",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {mediaFileName || "Document_Catalog.pdf"}
                  </div>
                  <div style={{ fontSize: "9px", color: "#64748b" }}>
                    {mediaFileSize ? `${mediaFileSize} • PDF` : "1 page • PDF"}
                  </div>
                </div>
              </div>
            )}

          {/* LOCATION */}
          {!isAuth && headerType === "LOCATION" && (
            <div
              style={{
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                overflow: "hidden",
                border: "1px solid #d1d5db",
                marginBottom: "8px",
              }}
            >
              <div
                style={{
                  height: "75px",
                  backgroundColor: "#e2e8f0",
                  backgroundImage: "radial-gradient(#94a3b8 1.5px, transparent 1.5px)",
                  backgroundSize: "12px 12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    backgroundColor: "#ef4444",
                    color: "#ffffff",
                    borderRadius: "50%",
                    width: "26px",
                    height: "26px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.25)",
                  }}
                >
                  📍
                </div>
              </div>
              <div style={{ padding: "6px 8px" }}>
                <div style={{ fontSize: "11px", fontWeight: "bold", color: "#1e293b", lineHeight: "1.2" }}>
                  {locationData?.name || headerText || "Location Name"}
                </div>
                <div style={{ fontSize: "9px", color: "#64748b", marginTop: "2px", lineHeight: "1.2" }}>
                  {locationData?.address || "Address will appear here"}
                </div>
              </div>
            </div>
          )}

            {/* BODY */}
            <div
              style={{
                fontSize: "12px",
                lineHeight: "1.4",
                whiteSpace: "pre-wrap",
                color: "#1e293b",
              }}
              dangerouslySetInnerHTML={{
                __html: isAuth
                  ? formatWhatsAppText(bodyText || authBody, samples)
                  : (bodyText ? formatWhatsAppText(bodyText, samples) : "Your message content will appear here")
              }}
            />

            {/* FOOTER */}
            {(isAuth ? (addExpiryTime && (footerText || authFooter)) : footerText) && (
              <div
                style={{
                  fontSize: "10px",
                  color: "#64748b",
                  marginTop: "6px",
                  lineHeight: "1.3",
                }}
              >
                {isAuth ? (footerText || authFooter) : footerText}
              </div>
            )}

            {/* BUTTONS */}
            {isAuth ? (
              <div style={{ marginTop: "8px" }}>
                {authOtpType === "ONE_TAP" ? (
                  <div
                    style={{
                      background: "#ffffff",
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      padding: "7px 10px",
                      marginTop: "4px",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#0b5cff",
                      textAlign: "center",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                    }}
                  >
                    <span>{authAutofillText || "Autofill"}</span>
                  </div>
                ) : (
                  <div
                    style={{
                      background: "#ffffff",
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      padding: "7px 10px",
                      marginTop: "4px",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#0b5cff",
                      textAlign: "center",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                    }}
                  >
                    <svg
                      style={{ width: "13px", height: "13px", fill: "currentColor", flexShrink: 0 }}
                      viewBox="0 0 24 24"
                    >
                      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                    </svg>
                    <span>{authCopyCodeText || "Copy code"}</span>
                  </div>
                )}
              </div>
            ) : buttons.length > 0 && (
              <div style={{ marginTop: "8px" }}>
                {buttons.map((btn, i) => {
                  const bType = (btn.type || "").toUpperCase();
                  const isOtp = bType === "OTP";
                  const isOneTap = isOtp && btn.otp_type === "ONE_TAP";
                  const btnLabel = isOneTap
                    ? (btn.autofill_text || btn.text || "Autofill")
                    : (btn.text || "Button");

                  return (
                    <div
                      key={i}
                      style={{
                        background: "#fff",
                        border: "1px solid #ddd",
                        borderRadius: "6px",
                        padding: "6px",
                        marginTop: "6px",
                        fontSize: "11px",
                        color: "#0b5cff",
                        textAlign: "center",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                      }}
                    >
                      {isOtp && !isOneTap && (
                        <svg
                          style={{ width: "12px", height: "12px", fill: "currentColor" }}
                          viewBox="0 0 24 24"
                        >
                          <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                        </svg>
                      )}
                      <span>{btnLabel}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* TIME */}
            <div
              style={{
                fontSize: "9px",
                color: "#666",
                textAlign: "right",
                marginTop: "4px",
              }}
            >
              10:51 AM ✓✓
            </div>
          </div>
        </div>

        {/* INPUT */}
        <div style={{ padding: "8px", background: "#f0f0f0", flexShrink: 0 }}>
          <div
            style={{
              background: "#fff",
              borderRadius: "16px",
              padding: "6px 10px",
              fontSize: "11px",
              color: "#999",
            }}
          >
            Type a message…
          </div>
        </div>
      </div>
    </div>
  );
}
